package com.dotpx.ambient

import bio.aq.audio.CrossfadeSource
import kotlin.concurrent.atomics.AtomicBoolean
import kotlin.concurrent.atomics.AtomicReference
import kotlin.concurrent.atomics.ExperimentalAtomicApi
import kotlin.math.ceil
import kotlin.random.Random
import kotlin.time.TimeSource

/**
 * Shared transport/state machine. Each native UI calls [tick] while visible.
 * Platform code remains a PCM sink and does not own app behavior.
 */
@OptIn(ExperimentalAtomicApi::class)
class AmbientController internal constructor(
    private val dwellSeconds: Int,
    private val morphSeconds: Int,
    private val nowMillis: () -> Long,
    private val nextSeed: () -> ULong,
    private val nextSceneIndex: (Int) -> Int,
    private val outputFactory: ((FloatArray, Int) -> Unit) -> ControllerAudioOutput,
    private val onPlaybackLimitExpired: (() -> Unit)? = null,
) {
    constructor() : this(
        dwellSeconds = 30,
        morphSeconds = 30,
        nowMillis = monotonicClock(),
        nextSeed = { Random.nextLong().toULong() },
        nextSceneIndex = { bound -> Random.nextInt(bound) },
        outputFactory = ::NativeControllerAudioOutput,
    )

    constructor(onPlaybackLimitExpired: () -> Unit) : this(
        dwellSeconds = 30,
        morphSeconds = 30,
        nowMillis = monotonicClock(),
        nextSeed = { Random.nextLong().toULong() },
        nextSceneIndex = { bound -> Random.nextInt(bound) },
        outputFactory = ::NativeControllerAudioOutput,
        onPlaybackLimitExpired = onPlaybackLimitExpired,
    )

    private val activeSource = AtomicReference<PcmRenderer>(SilencePcmRenderer)
    private val sourceCrossfade = CrossfadeSource(
        initialSource = SilencePcmRenderer,
        transitionFrames = SOURCE_CROSSFADE_FRAMES,
    )
    private val playbackEndsAtMillis = AtomicReference<Long?>(null)
    private val playbackLimitDurationMillis = AtomicReference<Long?>(null)
    private val playbackLimitExpired = AtomicBoolean(false)
    private val fadeOutEnabled = AtomicBoolean(false)
    private val outputGain = AtomicReference(DEFAULT_OUTPUT_GAIN)
    private val output = outputFactory { buffer, frames ->
        val now = nowMillis()
        val deadline = playbackEndsAtMillis.load()
        val expired = deadline?.let { now >= it } == true
        if (expired) {
            buffer.fill(0f, 0, (frames * AmbientChannelCount).coerceAtMost(buffer.size))
            if (playbackLimitExpired.compareAndSet(expectedValue = false, newValue = true)) {
                onPlaybackLimitExpired?.invoke()
            }
        } else {
            sourceCrossfade.render(buffer, frames)
            val duration = playbackLimitDurationMillis.load()
            val sampleCount = (frames * AmbientChannelCount).coerceAtMost(buffer.size)
            if (fadeOutEnabled.load() && deadline != null && duration != null && duration > 0L) {
                val gain = ((deadline - now).coerceIn(0L, duration).toDouble() / duration).toFloat()
                for (index in 0 until sampleCount) buffer[index] *= gain
            }
            val gain = outputGain.load()
            for (index in 0 until sampleCount) {
                buffer[index] = (buffer[index] * gain).coerceIn(-1f, 1f)
            }
        }
    }

    private var renderer: MorphingAmbienceRenderer? = null
    private val materialCycle = SoundMaterialCycle()
    private var state = AmbientControllerState()
    private var advanceAtMillis: Long? = null
    private var pausedAdvanceMillis: Long? = null
    private var pausedPlaybackLimitMillis: Long? = null
    private var previewEndsAtMillis: Long? = null
    private var lastPlaybackScene: AmbienceScene? = null
    private var lastPlaybackWasPreset = false
    private var resumeAfterInterruption = false
    private var hasPremiumAccess = false
    private var requestedPlaybackLimitMinutes = FREE_PLAYBACK_LIMIT_MINUTES

    val currentState: AmbientControllerState get() = state

    internal val debugActiveTexture: TextureKind?
        get() = renderer?.debugActiveTexture
    internal val debugActiveEvents: List<EventKind>
        get() = renderer?.let { active ->
            listOf(active.debugActiveEvent, active.debugActiveSecondaryEvent)
                .filter { it != EventKind.None }
        } ?: emptyList()
    internal val debugRemainingTextureCount: Int
        get() = materialCycle.availableTextures().size

    /**
     * Applies the store entitlement and the user's preferred sleep timer.
     * A value of 0 means unlimited, but only while premium access is active.
     */
    fun configurePlaybackAccess(
        isPremium: Boolean,
        requestedLimitMinutes: Int,
        fadeOut: Boolean = false,
    ): AmbientControllerState {
        val normalizedLimitMinutes = requestedLimitMinutes.coerceAtLeast(0)
        val samePlaybackAccess =
            hasPremiumAccess == isPremium &&
                this.requestedPlaybackLimitMinutes == normalizedLimitMinutes
        fadeOutEnabled.store(fadeOut)
        if (samePlaybackAccess) return state
        hasPremiumAccess = isPremium
        this.requestedPlaybackLimitMinutes = normalizedLimitMinutes
        playbackLimitExpired.store(false)
        when {
            state.playback.phase.isAudible -> schedulePlaybackLimit(nowMillis())

            state.playback.phase == AmbiencePlaybackPhase.Paused -> {
                val duration = effectivePlaybackLimitMillis()
                pausedPlaybackLimitMillis = duration
                playbackEndsAtMillis.store(null)
                playbackLimitDurationMillis.store(duration)
            }

            else -> {
                pausedPlaybackLimitMillis = null
                playbackEndsAtMillis.store(null)
                playbackLimitDurationMillis.store(null)
            }
        }
        return state
    }

    fun setOutputGain(gain: Float) {
        outputGain.store(gain.coerceIn(MINIMUM_OUTPUT_GAIN, MAXIMUM_OUTPUT_GAIN))
    }

    fun setContinuousEngineEnabled(enabled: Boolean) {
        output.setContinuousEngineEnabled(enabled)
    }

    /** Explicit system stop clears the renderer and any pending interruption resume. */
    fun stop(): AmbientControllerState = stopPlayback()

    /** Idempotent remote play, including restarting the last ambience after a full stop. */
    fun resumePlayback(): AmbientControllerState {
        if (state.previewVisible) return state
        return when (state.playback.phase) {
            AmbiencePlaybackPhase.Playing, AmbiencePlaybackPhase.Morphing -> state

            AmbiencePlaybackPhase.Paused -> resume()

            AmbiencePlaybackPhase.Interrupted -> {
                cancelInterruptionResume()
                resume()
            }

            AmbiencePlaybackPhase.Stopped -> lastPlaybackScene?.let {
                playScene(it, usePresetCombination = lastPlaybackWasPreset)
            } ?: playRandom()
        }
    }

    fun togglePlayback(): AmbientControllerState = when (state.playback.phase) {
        AmbiencePlaybackPhase.Stopped -> playRandom()

        AmbiencePlaybackPhase.Playing,
        AmbiencePlaybackPhase.Morphing,
        -> pause()

        AmbiencePlaybackPhase.Paused -> resume()

        AmbiencePlaybackPhase.Interrupted -> cancelInterruptionResume()
    }

    fun playRandom(): AmbientControllerState = playScene(
        scene = randomScene(excluding = state.playback.scene),
        usePresetCombination = false,
    )

    fun playPreset(scene: AmbienceScene): AmbientControllerState = playScene(scene, usePresetCombination = true)

    private fun playScene(scene: AmbienceScene, usePresetCombination: Boolean): AmbientControllerState {
        if (state.playback.phase == AmbiencePlaybackPhase.Stopped) {
            materialCycle.reset()
        }
        val previousSource = activeSource.load()
        val seed = nextSeed()
        val newRenderer = MorphingAmbienceRenderer(
            sampleRate = AmbientSampleRate.toDouble(),
            seed = seed,
            scene = scene,
            usePresetCombination = usePresetCombination,
            materialCycle = materialCycle,
        )
        setActiveSource(newRenderer)
        return try {
            output.start()
            renderer = newRenderer
            previewEndsAtMillis = null
            val now = nowMillis()
            advanceAtMillis = now + dwellSeconds * 1_000L
            pausedAdvanceMillis = null
            pausedPlaybackLimitMillis = null
            schedulePlaybackLimit(now)
            state = state.copy(
                playback = AmbiencePlaybackSnapshot(
                    phase = AmbiencePlaybackPhase.Playing,
                    scene = scene,
                    seed = seed,
                    nextScene = randomScene(excluding = scene),
                    secondsUntilAdvance = dwellSeconds,
                    matchesPresetCombination = newRenderer.status.activeMatchesPresetCombination,
                ),
                visualization = newRenderer.visualFrame,
                previewing = null,
            )
            state
        } catch (error: Throwable) {
            setActiveSource(previousSource)
            if (!state.playback.phase.isAudible) runCatching { output.pause() }
            publishError(error)
        }
    }

    fun pause(): AmbientControllerState {
        if (!state.playback.phase.isAudible) return state
        val now = nowMillis()
        val remaining = advanceAtMillis?.let { (it - now).coerceAtLeast(0) }
        val playbackLimitRemaining = playbackEndsAtMillis.load()?.let { (it - now).coerceAtLeast(0) }
        return try {
            output.pause()
            pausedAdvanceMillis = remaining
            pausedPlaybackLimitMillis = playbackLimitRemaining
            advanceAtMillis = null
            playbackEndsAtMillis.store(null)
            state = state.copy(
                playback = state.playback.copy(
                    phase = AmbiencePlaybackPhase.Paused,
                    secondsUntilAdvance = remaining?.toWholeSecondsCeil(),
                    errorMessage = null,
                ),
            )
            state
        } catch (error: Throwable) {
            publishError(error)
        }
    }

    fun resume(): AmbientControllerState {
        val ambientRenderer = renderer ?: return playRandom()
        if (state.playback.phase != AmbiencePlaybackPhase.Paused || state.previewVisible) return state
        val previousSource = activeSource.load()
        setActiveSource(ambientRenderer)
        return try {
            output.start()
            val remaining = pausedAdvanceMillis ?: (dwellSeconds * 1_000L)
            advanceAtMillis = if (ambientRenderer.status.isMorphing) null else nowMillis() + remaining
            pausedAdvanceMillis = null
            val limitRemaining = pausedPlaybackLimitMillis ?: effectivePlaybackLimitMillis()
            if (playbackLimitDurationMillis.load() == null) {
                playbackLimitDurationMillis.store(effectivePlaybackLimitMillis())
            }
            playbackEndsAtMillis.store(limitRemaining?.let { nowMillis() + it })
            pausedPlaybackLimitMillis = null
            playbackLimitExpired.store(false)
            val renderStatus = ambientRenderer.status
            state = state.copy(
                playback = state.playback.copy(
                    phase = if (renderStatus.isMorphing) {
                        AmbiencePlaybackPhase.Morphing
                    } else {
                        AmbiencePlaybackPhase.Playing
                    },
                    scene = renderStatus.activeScene,
                    seed = renderStatus.activeSeed,
                    secondsUntilAdvance = if (renderStatus.isMorphing) null else remaining.toWholeSecondsCeil(),
                    errorMessage = null,
                    matchesPresetCombination = renderStatus.activeMatchesPresetCombination,
                ),
            )
            state
        } catch (error: Throwable) {
            setActiveSource(previousSource)
            runCatching { output.pause() }
            publishError(error)
        }
    }

    /** Shuffle intentionally works only while paused, matching the original app. */
    fun shuffle(): AmbientControllerState {
        if (state.playback.phase != AmbiencePlaybackPhase.Paused || state.previewVisible) return state
        return playRandom()
    }

    fun showPreview(): AmbientControllerState {
        if (state.playback.phase.isAudible) {
            pause()
            if (state.playback.phase.isAudible) return state
        }
        return try {
            output.pause()
            state = state.copy(previewVisible = true, previewing = null)
            state
        } catch (error: Throwable) {
            publishError(error)
        }
    }

    fun hidePreview(): AmbientControllerState = try {
        output.pause()
        previewEndsAtMillis = null
        renderer?.let(::setActiveSource) ?: setActiveSource(SilencePcmRenderer)
        state = state.copy(previewVisible = false, previewing = null)
        state
    } catch (error: Throwable) {
        publishError(error)
    }

    fun playPreview(item: SoundPreviewItem): AmbientControllerState {
        if (!state.previewVisible) return state
        val previousSource = activeSource.load()
        val preview = SoundPreviewRenderer(
            item = item,
            sampleRate = AmbientSampleRate.toDouble(),
            seed = nextSeed(),
        )
        setActiveSource(preview)
        return try {
            output.start()
            previewEndsAtMillis = nowMillis() + ((item.maximumDurationSeconds + 0.35) * 1_000).toLong()
            state = state.copy(
                playback = state.playback.copy(errorMessage = null),
                previewing = item,
            )
            state
        } catch (error: Throwable) {
            setActiveSource(previousSource)
            runCatching { output.pause() }
            publishError(error)
        }
    }

    fun stopPreview(): AmbientControllerState {
        if (state.previewing == null) return state
        return try {
            output.pause()
            previewEndsAtMillis = null
            state = state.copy(previewing = null)
            state
        } catch (error: Throwable) {
            publishError(error)
        }
    }

    fun clearError(): AmbientControllerState {
        state = state.copy(playback = state.playback.copy(errorMessage = null))
        return state
    }

    /** Called by the iOS host when AVAudioSession begins an interruption. */
    fun audioInterruptionBegan(): AmbientControllerState {
        resumeAfterInterruption = state.playback.phase.isAudible && !state.previewVisible
        return when {
            state.previewing != null -> stopPreview()

            resumeAfterInterruption -> {
                val paused = pause()
                if (paused.playback.phase == AmbiencePlaybackPhase.Paused) {
                    state = paused.copy(
                        playback = paused.playback.copy(phase = AmbiencePlaybackPhase.Interrupted),
                    )
                }
                state
            }

            else -> state
        }
    }

    /** Resumes only playback that was running when the system interruption began. */
    fun audioInterruptionEnded(shouldResume: Boolean): AmbientControllerState {
        val resume = resumeAfterInterruption
        resumeAfterInterruption = false
        if (!resume) return state
        return if (shouldResume) {
            state = state.copy(
                playback = state.playback.copy(phase = AmbiencePlaybackPhase.Paused),
            )
            resume()
        } else {
            stopPlayback()
        }
    }

    /** Prevents a pending system resume from restarting audio after the app backgrounds. */
    fun applicationBecameInactive(allowBackgroundPlayback: Boolean = false): AmbientControllerState {
        resumeAfterInterruption = false
        return when {
            state.previewing != null -> stopPreview()

            allowBackgroundPlayback && hasPremiumAccess && state.playback.phase.isAudible -> state

            state.playback.phase.isAudible -> pause()

            state.playback.phase == AmbiencePlaybackPhase.Interrupted -> {
                state = state.copy(
                    playback = state.playback.copy(phase = AmbiencePlaybackPhase.Paused),
                )
                state
            }

            else -> state
        }
    }

    /** Matches the original app: losing the active output route stops the session. */
    fun audioRouteBecameUnavailable(): AmbientControllerState {
        resumeAfterInterruption = false
        return stopPlayback()
    }

    /** AVAudioSession media services reset invalidates the complete native graph. */
    fun mediaServicesWereReset(): AmbientControllerState {
        resumeAfterInterruption = false
        return stopPlayback("オーディオ機能が再起動されました。もう一度再生してください。")
    }

    /** Rebuilds a stopped AVAudioEngine while preserving the shared renderer and schedule. */
    fun recoverAudioEngine(): AmbientControllerState {
        if (!state.playback.phase.isAudible) return state
        return try {
            output.stop()
            output.start()
            state = state.copy(playback = state.playback.copy(errorMessage = null))
            state
        } catch (error: Throwable) {
            runCatching { output.pause() }
            handleAsynchronousFailure(error, nowMillis())
        }
    }

    fun tick(): AmbientControllerState {
        val now = nowMillis()
        output.consumeFailure()?.let { return handleAsynchronousFailure(it, now) }

        if (state.playback.phase.isAudible &&
            (
                playbackLimitExpired.exchange(false) ||
                    playbackEndsAtMillis.load()?.let { now >= it } == true
                )
        ) {
            return stopPlayback()
        }

        if (state.previewVisible) {
            if (previewEndsAtMillis?.let { now >= it } == true) stopPreview()
            return state
        }

        val ambientRenderer = renderer ?: return state
        var playback = state.playback
        val renderStatus = ambientRenderer.status

        if (playback.phase == AmbiencePlaybackPhase.Morphing && !renderStatus.isMorphing) {
            val activeScene = renderStatus.activeScene
            val activeSeed = renderStatus.activeSeed
            advanceAtMillis = now + dwellSeconds * 1_000L
            playback = playback.copy(
                phase = AmbiencePlaybackPhase.Playing,
                scene = activeScene,
                seed = activeSeed,
                nextScene = randomScene(excluding = activeScene),
                secondsUntilAdvance = dwellSeconds,
                matchesPresetCombination = renderStatus.activeMatchesPresetCombination,
            )
        }

        val deadline = advanceAtMillis
        if (playback.phase == AmbiencePlaybackPhase.Playing && deadline != null && now >= deadline) {
            val target = playback.nextScene ?: randomScene(excluding = playback.scene)
            val targetSeed = nextSeed()
            val command = ambientRenderer.requestMorph(
                scene = target,
                seed = targetSeed,
                durationFrames = morphSeconds.toLong() * AmbientSampleRate,
            )
            if (command != null) {
                advanceAtMillis = null
                playback = playback.copy(
                    phase = AmbiencePlaybackPhase.Morphing,
                    nextScene = target,
                    secondsUntilAdvance = null,
                )
            }
        } else if (playback.phase == AmbiencePlaybackPhase.Playing && deadline != null) {
            playback = playback.copy(secondsUntilAdvance = (deadline - now).toWholeSecondsCeil())
        }

        state = state.copy(
            playback = playback,
            visualization = ambientRenderer.visualFrame,
        )
        return state
    }

    fun release() {
        try {
            runCatching { output.release() }
        } finally {
            setActiveSource(SilencePcmRenderer)
            renderer = null
            advanceAtMillis = null
            pausedAdvanceMillis = null
            pausedPlaybackLimitMillis = null
            playbackEndsAtMillis.store(null)
            playbackLimitDurationMillis.store(null)
            playbackLimitExpired.store(false)
            previewEndsAtMillis = null
            resumeAfterInterruption = false
            materialCycle.reset()
            state = AmbientControllerState()
        }
    }

    private fun stopPlayback(errorMessage: String? = null): AmbientControllerState {
        if (!state.previewVisible && state.playback.scene != null) {
            lastPlaybackScene = state.playback.scene
            lastPlaybackWasPreset = state.playback.matchesPresetCombination
        }
        val stopFailure = runCatching { output.stop() }.exceptionOrNull()
        setActiveSource(SilencePcmRenderer)
        renderer = null
        advanceAtMillis = null
        pausedAdvanceMillis = null
        pausedPlaybackLimitMillis = null
        playbackEndsAtMillis.store(null)
        playbackLimitDurationMillis.store(null)
        playbackLimitExpired.store(false)
        previewEndsAtMillis = null
        resumeAfterInterruption = false
        materialCycle.reset()
        state = AmbientControllerState(
            playback = AmbiencePlaybackSnapshot(
                errorMessage = errorMessage ?: stopFailure?.message,
            ),
            previewVisible = state.previewVisible,
        )
        return state
    }

    private fun handleAsynchronousFailure(error: Throwable, now: Long): AmbientControllerState {
        resumeAfterInterruption = false
        runCatching { output.pause() }
        pausedAdvanceMillis = advanceAtMillis?.let { (it - now).coerceAtLeast(0) }
        advanceAtMillis = null
        pausedPlaybackLimitMillis = playbackEndsAtMillis.load()?.let { (it - now).coerceAtLeast(0) }
        playbackEndsAtMillis.store(null)
        previewEndsAtMillis = null
        val failedPhase = if (renderer == null) {
            AmbiencePlaybackPhase.Stopped
        } else {
            AmbiencePlaybackPhase.Paused
        }
        state = state.copy(
            playback = state.playback.copy(
                phase = failedPhase,
                secondsUntilAdvance = pausedAdvanceMillis?.toWholeSecondsCeil(),
                errorMessage = error.message ?: "音声の再生中にエラーが発生しました",
            ),
            previewing = null,
        )
        return state
    }

    private fun cancelInterruptionResume(): AmbientControllerState {
        resumeAfterInterruption = false
        state = state.copy(
            playback = state.playback.copy(phase = AmbiencePlaybackPhase.Paused),
        )
        return state
    }

    private fun publishError(error: Throwable): AmbientControllerState {
        state = state.copy(
            playback = state.playback.copy(
                errorMessage = error.message ?: "音声を開始できませんでした",
            ),
        )
        return state
    }

    private fun setActiveSource(source: PcmRenderer) {
        activeSource.store(source)
        sourceCrossfade.replaceSource(source)
    }

    private fun randomScene(excluding: AmbienceScene?): AmbienceScene {
        val scenes = AmbienceScene.entries
        if (scenes.size <= 1) return scenes.first()
        var candidate = scenes[nextSceneIndex(scenes.size).mod(scenes.size)]
        if (candidate == excluding) candidate = scenes[(candidate.ordinal + 1) % scenes.size]
        return candidate
    }

    private fun schedulePlaybackLimit(now: Long) {
        pausedPlaybackLimitMillis = null
        val duration = effectivePlaybackLimitMillis()
        playbackLimitDurationMillis.store(duration)
        playbackEndsAtMillis.store(duration?.let { now + it })
        playbackLimitExpired.store(false)
    }

    private fun effectivePlaybackLimitMillis(): Long? {
        val minutes = if (hasPremiumAccess) {
            requestedPlaybackLimitMinutes.takeIf { it > 0 }
        } else {
            FREE_PLAYBACK_LIMIT_MINUTES
        }
        return minutes?.toLong()?.times(60_000L)
    }

    private companion object {
        const val FREE_PLAYBACK_LIMIT_MINUTES = 60
        const val MINIMUM_OUTPUT_GAIN = 0f
        const val MAXIMUM_OUTPUT_GAIN = 3f
        const val DEFAULT_OUTPUT_GAIN = 2.5f
        const val SOURCE_CROSSFADE_FRAMES = AmbientSampleRate / 50
    }
}

private val AmbiencePlaybackPhase.isAudible: Boolean
    get() = this == AmbiencePlaybackPhase.Playing || this == AmbiencePlaybackPhase.Morphing

private object SilencePcmRenderer : PcmRenderer {
    override fun render(interleaved: FloatArray, frameCount: Int) {
        interleaved.fill(0f, 0, (frameCount * AmbientChannelCount).coerceAtMost(interleaved.size))
    }
}

private fun Long.toWholeSecondsCeil(): Int = ceil(this / 1_000.0).toInt().coerceAtLeast(0)

private fun monotonicClock(): () -> Long {
    val origin = TimeSource.Monotonic.markNow()
    return { origin.elapsedNow().inWholeMilliseconds }
}
